/* ==========================
 * 黑五活动页核心脚本 - 架构重构版
 * 架构：分层设计 + 依赖注入 + 配置驱动
 * ========================== */

/* --------------------------
 * 📦 数据层（Data Layer）
 * -------------------------- */

// ① 配置服务：统一管理所有配置源
class ConfigService {
  constructor() {
    this.config = this._loadConfig();
    this._validateConfig();
  }

  _loadConfig() {
    const defaults = {
      enabled: true,
      product1Title: "Flagship Bumblebee Auto-Converting Robot",
      product2Title: "Flagship Megatron Auto-Converting Robot",
      collectionHandle: "",
      limit: 100,
      tiers: "",
      tiersTitle: "",
      excludeTitles: "",
      // 新增：商品配置规则
      productRules: {
        variantExpandTargets: ["Toy Story Buzz Lightyear Robot"], // 需要展开变体的商品
        variantSelectableTargets: ["Interstellar Scout K1 Programming Robot"], // 可选变体的商品
      },
      // 新增：颜色映射配置
      colorMapping: {
        white: "white",
        blue: "blue",
        red: "red",
        black: "black",
      },
      // 新增：排除商品的额外变体映射（用于自动添加赠品）
      excludedProductBonusVariants: {
        "flagship megatron auto-converting robot": '',
        "flagship grimlock auto-converting robot (collector's edition)": '',
        "flagship bumblebee auto-converting robot": '',
      },
    };

    const themeConfig = this._loadThemeConfig();
    const globalConfig = window.BF_CONFIG || {};

    return Object.assign({}, defaults, themeConfig, globalConfig);
  }

  _loadThemeConfig() {
    try {
      const el = document.querySelector("[data-bf-config]");
      return JSON.parse(el?.textContent || "{}");
    } catch (e) {
      console.warn("主题配置解析失败:", e);
      return {};
    }
  }

  _validateConfig() {
    if (!this.config.collectionHandle) {
      console.warn("【配置警告】未设置 collectionHandle");
    }
  }

  get(key, defaultValue = null) {
    return this.config[key] ?? defaultValue;
  }

  isEnabled() {
    return this.config.enabled === true;
  }

  // 检查商品是否需要展开变体
  shouldExpandVariants(productTitle) {
    return this.config.productRules.variantExpandTargets.includes(productTitle);
  }

  // 检查商品是否显示变体选择器
  shouldShowVariantSelector(productTitle) {
    return this.config.productRules.variantSelectableTargets.includes(
      productTitle,
    );
  }

  // 获取颜色CSS类名
  getColorClass(variantTitle) {
    const lowerTitle = (variantTitle || "").toLowerCase();
    const colorMapping = this.config.colorMapping;

    for (const [key, value] of Object.entries(colorMapping)) {
      if (lowerTitle.includes(key)) {
        return value;
      }
    }
    return "white"; // 默认颜色
  }

  // 获取排除商品的额外赠品变体ID
  getBonusVariantId(productTitle) {
    const lowerTitle = (productTitle || "").toLowerCase();
    const bonusMapping = this.config.excludedProductBonusVariants;
    return bonusMapping[lowerTitle] || null;
  }
}

// ② 商品服务：API调用与缓存管理
class ProductService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map(); // 防止重复请求
  }

  async fetchProduct(handle) {
    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    // 防止并发重复请求
    if (this.pendingRequests.has(handle)) {
      return this.pendingRequests.get(handle);
    }

    const request = this._doFetchProduct(handle);
    this.pendingRequests.set(handle, request);

    try {
      const data = await request;
      this.cache.set(handle, data);
      return data;
    } finally {
      this.pendingRequests.delete(handle);
    }
  }

  async _doFetchProduct(handle) {
    const url = `/products/${handle}.js`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Product fetch failed: ${handle}`);
    return await res.json();
  }

  async fetchCollection(handle, limit = 100) {
    const url = `/collections/${handle}/products.json?limit=${limit}`;
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
    });

    if (!res.ok) return [];

    const json = await res.json();
    return (json.products || []).map((p) => ({
      handle: p.handle,
      title: p.title,
      images: (p.images || []).map((i) => i.src),
      featured_image: p.image?.src,
    }));
  }

  // 批量预加载（性能优化）
  async preloadProducts(handles) {
    return Promise.allSettled(handles.map((h) => this.fetchProduct(h)));
  }
}

// ③ 状态存储：集中式状态管理
class StateStore {
  constructor() {
    this.state = {
      products: [],
      selections: {
        1: { product: null, variant: null },
        2: { product: null, variant: null },
      },
    };
    this.listeners = new Set();
  }

  // 订阅状态变化
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  _notify(type, payload) {
    this.listeners.forEach((cb) => cb({ type, payload }));
  }

  setProducts(products) {
    this.state.products = products;
    this._notify("PRODUCTS_LOADED", products);
  }

  getProducts() {
    return this.state.products;
  }

  selectProduct(slot, product, variant = null) {
    this.state.selections[slot] = { product, variant };
    this._notify("PRODUCT_SELECTED", { slot, product, variant });
  }

  getSelection(slot) {
    return this.state.selections[slot];
  }

  getAllSelections() {
    return [this.state.selections[1], this.state.selections[2]];
  }

  updateVariant(slot, variant) {
    if (this.state.selections[slot]) {
      this.state.selections[slot].variant = variant;
      this._notify("VARIANT_CHANGED", { slot, variant });
    }
  }
}

/* --------------------------
 * 🧠 业务逻辑层（Business Layer）
 * -------------------------- */

// ④ 阶梯满减引擎
class DiscountEngine {
  constructor(configService, stateStore) {
    this.config = configService;
    this.store = stateStore;
    this.tiers = [];
    this.excludedTitles = new Set();
  }

  parseTiers(tiersConfig) {
    this.tiers = (tiersConfig || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map(this._parseTierLine)
      .filter((t) => t && t.off && t.over)
      .sort((a, b) => a.over - b.over);
  }

  _parseTierLine(line) {
    const [off, over] = line
      .split("|")
      .map((v) => parseFloat(v.replace(/,/g, "")) * 100);
    return { off, over };
  }

  setExcludedTitles(titles) {
    this.excludedTitles = new Set(
      (Array.isArray(titles) ? titles : titles.split(/\n+/))
        .map((t) => (t || "").trim().toLowerCase())
        .filter(Boolean),
    );
  }

  calculateTotals() {
    const selections = this.store.getAllSelections();

    return selections.reduce(
      (acc, { product, variant }) => {
        if (!product) return acc;

        const effectiveVariant =
          variant || this._getDefaultVariant(product.variants);
        const price = effectiveVariant?.price ?? product.price ?? 0;
        const compare =
          effectiveVariant?.compare_at_price ??
          product.compare_at_price ??
          price;

        acc.price += price;
        acc.compare += compare;
        return acc;
      },
      { price: 0, compare: 0 },
    );
  }

  _getDefaultVariant(variants) {
    return variants?.find((v) => v.available) || variants?.[0];
  }

  calculateTierEligibleAmount(totalPrice) {
    const selections = this.store.getAllSelections();

    const excludedPrice = selections.reduce((sum, { product, variant }) => {
      if (!product) return sum;

      const title = (product.title || "").toLowerCase();
      if (!this.excludedTitles.has(title)) return sum;

      const effectiveVariant =
        variant || this._getDefaultVariant(product.variants);
      const price = effectiveVariant?.price ?? product.price ?? 0;
      return sum + price;
    }, 0);

    return Math.max(0, totalPrice - excludedPrice);
  }

  getAchievedTier(amount) {
    let achieved = null;
    for (const tier of this.tiers) {
      if (amount >= tier.over) achieved = tier;
      else break;
    }
    return achieved;
  }

  getNextTier(amount) {
    return this.tiers.find((t) => t.over > amount);
  }

  getTiers() {
    return this.tiers;
  }
}

// ⑤ 商品选择协调器
class ProductSelector {
  constructor(productService, stateStore, configService, discountEngine) {
    this.productService = productService;
    this.store = stateStore;
    this.config = configService;
    this.discount = discountEngine;
  }

  async selectProduct(slot, handle, variantId = null) {
    const product = await this.productService.fetchProduct(handle);

    let variant = null;
    if (variantId) {
      variant = product.variants?.find((v) => v.id === variantId);
    }

    if (!variant && product.variants?.length > 0) {
      variant =
        product.variants.find((v) => v.available) || product.variants[0];
    }

    this.store.selectProduct(slot, product, variant);
    return { product, variant };
  }

  changeVariant(slot, variantId) {
    const { product } = this.store.getSelection(slot);
    if (!product) return;

    const variant = product.variants?.find((v) => v.id === variantId);
    if (variant) {
      this.store.updateVariant(slot, variant);
    }
  }

  async loadProductList(collectionHandle, limit) {
    const products = await this.productService.fetchCollection(
      collectionHandle,
      limit,
    );
    this.store.setProducts(products);

    // 预加载需要展开变体的商品
    const targetsToPreload = products
      .filter((p) => this.config.shouldExpandVariants(p.title))
      .map((p) => p.handle);

    if (targetsToPreload.length > 0) {
      await this.productService.preloadProducts(targetsToPreload);
    }

    return products;
  }

  getCheckoutItems() {
    const selections = this.store.getAllSelections();
    const items = [];

    // 添加用户选择的商品变体
    selections.forEach(({ product, variant }) => {
      if (variant?.id) {
        items.push({ id: variant.id, quantity: 1 });

        // 检查是否需要添加额外的赠品变体
        if (product?.title) {
          const bonusVariantId = this.config.getBonusVariantId(product.title);
          if (bonusVariantId) {
            items.push({ id: bonusVariantId, quantity: 1 });
          }
        }
      }
    });

    return items;
  }
}

// ⑥ 结账协调器
class CheckoutCoordinator {
  constructor(productSelector) {
    this.selector = productSelector;
  }

  async checkout() {
    const items = this.selector.getCheckoutItems();

    if (items.length < 2) {
      throw new Error("请先选择两个产品");
    }

    try {
      // 添加到购物车
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error("添加购物车失败");

      window.location.href = "/cart";
      return false
      // 获取最新购物车数据
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();

      // 触发购物车更新事件
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartData,
          },
        }),
      );

      return cartData;
    } catch (error) {
      console.error("结账失败:", error);
      throw error;
    }
  }
}

/* --------------------------
 * 🎨 表现层（Presentation Layer）
 * -------------------------- */

// ⑦ 工具函数集合
const DOMUtils = {
  qs(sel, root = document) {
    return root.querySelector(sel);
  },

  qsa(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  },

  /**
   * 根据屏幕宽度选择响应式元素
   * @param {string} selector - CSS选择器
   * @param {number} breakpoint - 断点宽度（默认720px）
   * @param {Document|Element} root - 根元素
   * @returns {Element|null} - 返回匹配的元素
   */
  qsResponsive(selector, breakpoint = 720, root = document) {
    const elements = this.qsa(selector, root);
    if (elements.length === 0) return null;
    if (elements.length === 1) return elements[0];

    const viewportWidth = window.innerWidth;
    const index = viewportWidth > breakpoint ? 0 : 1;
    // console.log(index, viewportWidth, breakpoint);
    return elements[index] || elements[0];
  },

  money(cents = 0) {
    if (typeof cents !== "number" || isNaN(cents)) return "$0.00";
    const sign = cents < 0 ? "-" : "";
    return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  },

  moneyNoDecimals(cents = 0) {
    return (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 0,
    });
  },

  preloadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  },

  async swapImage(imgEl, newSrc) {
    if (!imgEl || !newSrc) return;
    if (imgEl.currentSrc === newSrc || imgEl.src === newSrc) return;

    try {
      await this.preloadImage(newSrc);
      imgEl.src = newSrc;
    } catch (e) {
      console.warn("图片加载失败:", newSrc, e);
    }
  },
};

// ⑧ DOM缓存管理器
class DOMCache {
  constructor() {
    this.cache = new Map();
  }

  get(key, selector) {
    if (!this.cache.has(key)) {
      const el = DOMUtils.qs(selector);
      if (el) this.cache.set(key, el);
    }
    return this.cache.get(key);
  }

  getSlotElements(slot) {
    const prefix = `slot-${slot}`;
    return {
      card: this.get(`${prefix}-card`, `[data-slot="${slot}"]`),
      image: this.get(
        `${prefix}-image`,
        `[data-slot="${slot}"] .christmas2025_combo-save__product-image`,
      ),
      name: this.get(
        `${prefix}-name`,
        `[data-slot="${slot}"] .christmas2025_combo-save__product-name`,
      ),
      price: this.get(
        `${prefix}-price`,
        `[data-slot="${slot}"] .christmas2025_combo-save__price-current`,
      ),
      compare: this.get(
        `${prefix}-compare`,
        `[data-slot="${slot}"] .christmas2025_combo-save__price-original`,
      ),
      dropdown: this.get(
        `${prefix}-dropdown`,
        `[data-slot="${slot}"] .bf-select-list`,
      ),
      label: this.get(
        `${prefix}-label`,
        `[data-slot="${slot}"] .christmas2025_combo-save__tag-text`,
      ),
      trigger: this.get(
        `${prefix}-trigger`,
        `[data-slot="${slot}"] .christmas2025_combo-save__product-tag`,
      ),
    };
  }

  clear() {
    this.cache.clear();
  }
}

// ⑨ 纯渲染函数集合
const Renderers = {
  renderTiers(tiers, titleText) {
    const titleEl = DOMUtils.qs(".bf-tiers-title");
    const listEl = DOMUtils.qs("[data-tiers]");

    if (titleEl && titleText) titleEl.textContent = titleText;
    if (!listEl) return;

    const html = tiers
      .map(
        (t) => `
      <li class="bf-tier" data-over="${t.over}" data-off="${t.off}">
        $${DOMUtils.moneyNoDecimals(t.off)} OFF Over $${DOMUtils.moneyNoDecimals(t.over)}
      </li>
    `,
      )
      .join("");

    listEl.innerHTML = html;
  },

  renderProductCard(elements, product, variant, config) {
    const { image, name, price, compare } = elements;

    // 渲染图片
    const imgUrl =
      variant?.featured_image?.src ||
      product.images?.[0] ||
      product.featured_image ||
      "";
    let imgEl = image?.querySelector("img");

    if (!imgEl && image) {
      imgEl = document.createElement("img");
      imgEl.alt = product.title || "";
      imgEl.loading = "eager";
      imgEl.decoding = "async";
      image.appendChild(imgEl);
    }

    if (imgEl && imgUrl) {
      imgEl.alt = product.title || "";
      DOMUtils.swapImage(imgEl, imgUrl);
    }

    // 渲染标题
    let displayName = product.title || "";
    if (variant?.title && variant.title !== "Default Title") {
      displayName = `${product.title} - ${variant.title}`;
    }
    if (name) name.textContent = displayName;

    // 渲染价格
    const currentPrice = variant?.price ?? product.price ?? 0;
    const comparePrice =
      variant?.compare_at_price ?? product.compare_at_price ?? 0;

    if (price) price.textContent = DOMUtils.money(currentPrice);
    if (compare) {
      compare.textContent =
        comparePrice > currentPrice ? DOMUtils.money(comparePrice) : "";
    }
  },

  renderVariantSelector(elements, product, currentVariant, config) {
    const { card, image } = elements;
    if (!card) return;

    // 检查是否应该显示变体选择器
    if (!config.shouldShowVariantSelector(product.title) || 
        !product.variants || 
        product.variants.length <= 1) {
      const existingSelector = card.querySelector(
        ".christmas2025_combo-save__color-selector",
      );
      if (existingSelector) existingSelector.remove();
      return;
    }

    let variantBox = card.querySelector(
      ".christmas2025_combo-save__color-selector",
    );

    if (!variantBox) {
      variantBox = document.createElement("div");
      variantBox.className = "christmas2025_combo-save__color-selector";
      if (image) image.appendChild(variantBox);
      else card.appendChild(variantBox);
    }

    // 生成颜色按钮
    const colorButtons = product.variants
      .map((v, idx) => {
        const colorClass = config.getColorClass(v.title);
        const isActive = v.id === currentVariant?.id;
        const activeClass = isActive
          ? `christmas2025_${colorClass}-active`
          : "";

        return `
        <span class="christmas2025_combo-save__color-${colorClass} ${activeClass}" 
              data-variant-id="${v.id}" 
              data-variant-index="${idx}">
          <span class="christmas2025_combo-save__color-${colorClass}-inner"></span>
        </span>
      `;
      })
      .join("");

    variantBox.innerHTML = `
      <span class="christmas2025_combo-save__color-label">Color ：</span>
      ${colorButtons}
    `;
  },

  async renderDropdownOptions(slot, products, productService, config) {
    const dropdown = DOMUtils.qs(
      `[data-slot="${slot}"] .bf-select-list`,
    );
    if (!dropdown) return;

    dropdown.innerHTML = "";
    const fragment = document.createDocumentFragment();

    for (const product of products) {
      // 检查是否需要展开变体
      if (config.shouldExpandVariants(product.title)) {
        try {
          const fullProduct = await productService.fetchProduct(product.handle);

          if (fullProduct.variants && fullProduct.variants.length > 1) {
            fullProduct.variants.forEach((variant) => {
              fragment.appendChild(
                this._createOptionElement(fullProduct, variant),
              );
            });
            continue;
          }
        } catch (e) {
          console.warn(`加载商品变体失败: ${product.title}`, e);
        }
      }

      // 普通商品选项
      fragment.appendChild(this._createOptionElement(product));
    }

    dropdown.appendChild(fragment);
  },

  _createOptionElement(product, variant = null) {
    const li = document.createElement("li");
    li.className = "bf-select-option";
    li.setAttribute("role", "option");
    li.dataset.handle = product.handle;

    if (variant) {
      li.dataset.variantId = variant.id;
      li.innerHTML = `<span>${product.title} - ${variant.title}</span>`;
    } else {
      li.innerHTML = `<span>${product.title}</span>`;
    }

    return li;
  },

  setSelectedOption(slot, title) {
    const card = DOMUtils.qs(`[data-slot="${slot}"]`);
    if (!card) return;

    const allOptions = card.querySelectorAll(".bf-select-option");
    allOptions.forEach((opt) => {
      const matches =
        opt.textContent.trim() === title ||
        opt.textContent.trim().startsWith(title + " - ");
      opt.classList.toggle("selected", matches);
    });
  },

  updateTotals(elements, totals, savings, achievedTier, nextTier, tierAmount) {
    const { total, save, saveNote, hint } = elements;

    const payableTotal = Math.max(0, totals.price - savings.extraOff);

    if (total) total.textContent = DOMUtils.money(payableTotal);
    if (save)
      save.textContent = `$${DOMUtils.moneyNoDecimals(savings.totalSavings)}`;

    // 更新节省说明
    if (saveNote) {
      const parts = [];
      if (savings.baseDiscount > 0) {
        parts.push(`$${DOMUtils.moneyNoDecimals(savings.baseDiscount)} OFF`);
      }
      if (savings.extraOff > 0) {
        parts.push(`EXTRA $${DOMUtils.moneyNoDecimals(savings.extraOff)}`);
      }
      saveNote.textContent = parts.length ? `(${parts.join(" + ")})` : "";
    }

    // 更新阶梯提示
    if (hint) {
      hint.textContent = this._getTierHintText(
        achievedTier,
        nextTier,
        tierAmount,
      );
    }
  },

  _getTierHintText(achieved, next, currentAmount) {
    if (!achieved && next) {
      const needed = next.over - currentAmount;
      return `Add ${DOMUtils.money(needed)} more to unlock an extra $${DOMUtils.moneyNoDecimals(next.off)} off.`;
    }

    if (achieved && next) {
      const needed = next.over - currentAmount;
      return `Extra $${DOMUtils.moneyNoDecimals(achieved.off)} unlocked - add ${DOMUtils.money(needed)} more to unlock $${DOMUtils.moneyNoDecimals(next.off)} off.`;
    }

    if (achieved && !next) {
      return `Extra $${DOMUtils.moneyNoDecimals(achieved.off)} unlocked — Maximum savings achieved!`;
    }

    return "";
  },
};

// ⑩ 事件总线
class EventBus {
  constructor() {
    this.handlers = new Map();
  }

  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event).add(handler);

    return () => this.off(event, handler);
  }

  off(event, handler) {
    this.handlers.get(event)?.delete(handler);
  }

  emit(event, data) {
    this.handlers.get(event)?.forEach((handler) => handler(data));
  }

  clear() {
    this.handlers.clear();
  }
}

// ⑪ UI控制器
class UIController {
  constructor(
    stateStore,
    productSelector,
    discountEngine,
    configService,
    checkoutCoordinator,
    productService,
  ) {
    this.store = stateStore;
    this.selector = productSelector;
    this.discount = discountEngine;
    this.config = configService;
    this.checkout = checkoutCoordinator;
    this.productService = productService;
    this.domCache = new DOMCache();
    this.eventBus = new EventBus();

    this._setupStateListeners();
  }

  _setupStateListeners() {
    this.store.subscribe(({ type, payload }) => {
      if (type === "PRODUCT_SELECTED") {
        this._onProductSelected(payload);
      } else if (type === "VARIANT_CHANGED") {
        this._onVariantChanged(payload);
      }
    });
  }

  _onProductSelected({ slot, product, variant }) {
    const elements = this.domCache.getSlotElements(slot);
    Renderers.renderProductCard(elements, product, variant, this.config);
    Renderers.renderVariantSelector(
      elements,
      product,
      variant,
      this.config,
    );
    this._updateTotals();
  }

  _onVariantChanged({ slot, variant }) {
    const { product } = this.store.getSelection(slot);
    const elements = this.domCache.getSlotElements(slot);
    Renderers.renderProductCard(elements, product, variant, this.config);
    Renderers.renderVariantSelector(
      elements,
      product,
      variant,
      this.config,
    );
    this._updateTotals();
  }

  _updateTotals() {
    const totals = this.discount.calculateTotals();
    const tierAmount = this.discount.calculateTierEligibleAmount(totals.price);
    const achievedTier = this.discount.getAchievedTier(tierAmount);
    const nextTier = this.discount.getNextTier(tierAmount);

    const extraOff = achievedTier?.off || 0;
    const baseDiscount = Math.max(totals.compare - totals.price, 0);
    const totalSavings = baseDiscount + extraOff;

    const elements = {
      total: DOMUtils.qs("[data-total]"),
      save: DOMUtils.qs("[data-save]"),
      saveNote: DOMUtils.qs("[data-save-note]"),
      hint: DOMUtils.qsResponsive("[data-tier-hint]", 720),
    };

    Renderers.updateTotals(
      elements,
      totals,
      { extraOff, baseDiscount, totalSavings },
      achievedTier,
      nextTier,
      tierAmount,
    );
    
    // 检查是否需要隐藏解锁提示
    this._toggleUnlockMessage();
  }

  async applyDefaultSelections() {
    const title1 = this.config.get("product1Title");
    const title2 = this.config.get("product2Title");

    const products = this.store.getProducts();
    const p1 = products.find((p) => p.title === title1);
    const p2 = products.find((p) => p.title === title2);

    if (p1 && p2) {
      await Promise.all([
        this.selector.selectProduct(1, p1.handle),
        this.selector.selectProduct(2, p2.handle),
      ]);

      // 更新下拉标签
      const label1 = this.domCache.get(
        "label-1",
        '[data-slot="1"] .christmas2025_combo-save__tag-text',
      );
      const label2 = this.domCache.get(
        "label-2",
        '[data-slot="2"] .christmas2025_combo-save__tag-text',
      );
      if (label1) label1.textContent = title1;
      if (label2) label2.textContent = title2;

      // 设置选中状态
      Renderers.setSelectedOption(1, title1);
      Renderers.setSelectedOption(2, title2);
    }
  }

  bindEvents() {
    this._bindDropdownEvents();
    this._bindVariantEvents();
    this._bindCheckoutEvent();
  }

  _bindDropdownEvents() {
    const productsRoot = document.getElementById("christmas-combo-save-products");
    if (!productsRoot) return;

    productsRoot.addEventListener("click", async (e) => {
      const trigger = e.target.closest(".christmas2025_combo-save__product-tag");
      if (trigger && !e.target.closest(".bf-select-option")) {
        this._toggleDropdown(trigger);
      }

      const option = e.target.closest(".bf-select-option");
      if (option) {
        await this._handleOptionSelect(option);
      }
    });

    // 全局关闭
    document.addEventListener("click", (e) => {
      if (e.target.closest(".christmas2025_combo-save__product-tag")) return;
      document
        .querySelectorAll(".christmas2025_combo-save__product-tag.open")
        .forEach((s) => {
          s.classList.remove("open");
          s.setAttribute("aria-expanded", "false");
        });
    });
  }

  _toggleDropdown(trigger) {
    const isOpen = trigger.classList.contains("open");
    
    // 关闭所有其他下拉
    document
      .querySelectorAll(".christmas2025_combo-save__product-tag.open")
      .forEach((s) => {
        if (s !== trigger) {
          s.classList.remove("open");
          s.setAttribute("aria-expanded", "false");
        }
      });

    // 切换当前下拉
    trigger.classList.toggle("open");
    trigger.setAttribute("aria-expanded", String(!isOpen));
  }

  async _handleOptionSelect(option) {
    const card = option.closest(".christmas2025_combo-save__product-card");
    const slot = Number(card?.dataset.slot);
    if (!slot) return;

    const handle = option.dataset.handle;
    const variantId = option.dataset.variantId;
    const optionText = option.textContent.trim();

    // 更新下拉标签
    const elements = this.domCache.getSlotElements(slot);
    if (elements.label) elements.label.textContent = optionText;

    // 设置选中状态
    const allOptions = card.querySelectorAll(".bf-select-option");
    allOptions.forEach((opt) => opt.classList.remove("selected"));
    option.classList.add("selected");

    // 关闭下拉
    if (elements.trigger) {
      elements.trigger.classList.remove("open");
      elements.trigger.setAttribute("aria-expanded", "false");
    }

    // 选择商品
    try {
      await this.selector.selectProduct(
        slot,
        handle,
        variantId ? Number(variantId) : null,
      );
      
      // 检查是否需要隐藏解锁提示
      this._toggleUnlockMessage();
    } catch (error) {
      console.error("选择商品失败:", error);
      alert("加载产品失败，请重试");
    }
  }

  _bindVariantEvents() {
    const productsRoot = DOMUtils.qs(".christmas2025_combo-save__products");
    if (!productsRoot) return;

    productsRoot.addEventListener("click", (e) => {
      const variantButton = e.target.closest("[data-variant-id]");
      if (!variantButton) return;

      const card = variantButton.closest(".christmas2025_combo-save__product-card");
      const slot = Number(card?.dataset.slot);
      if (!slot) return;

      const variantId = Number(variantButton.dataset.variantId);
      this.selector.changeVariant(slot, variantId);
    });
  }

  _bindCheckoutEvent() {
    const checkoutBtn = DOMUtils.qs("[data-checkout]");
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener("click", async () => {
      try {
        await this.checkout.checkout();
      } catch (error) {
        alert(error.message || "结账失败，请重试");
      }
    });
  }
  
  /**
   * 切换解锁提示的显示/隐藏状态
   * 当两个插槽都选择了排除商品时，隐藏解锁提示
   */
  _toggleUnlockMessage() {
    const unlockMessage = DOMUtils.qsResponsive(".extra-discount", 720);
    if (!unlockMessage) return;
    // console.log("检查解锁提示状态",unlockMessage);
    const selections = this.store.getAllSelections();
    const excludedTitles = this.discount.excludedTitles;
    
    // 检查两个插槽是否都选择了排除商品
    const bothExcluded = selections.every(({ product }) => {
      if (!product) return false;
      const title = (product.title || "").toLowerCase();
      return excludedTitles.has(title);
    });
    
    // 如果两个都是排除商品，隐藏；否则显示
    if (bothExcluded) {
      unlockMessage.style.display = "none";
    } else {
      unlockMessage.style.display = "";
    }
  }

  destroy() {
    this.eventBus.clear();
    this.domCache.clear();
  }
}

/* --------------------------
 * 🚀 应用主类（Application）
 * -------------------------- */
class BlackFridayApp {
  constructor() {
    // 数据层
    this.configService = new ConfigService();
    this.productService = new ProductService();
    this.stateStore = new StateStore();

    // 业务逻辑层
    this.discountEngine = new DiscountEngine(
      this.configService,
      this.stateStore,
    );
    this.productSelector = new ProductSelector(
      this.productService,
      this.stateStore,
      this.configService,
      this.discountEngine,
    );
    this.checkoutCoordinator = new CheckoutCoordinator(this.productSelector);

    // 表现层
    this.uiController = new UIController(
      this.stateStore,
      this.productSelector,
      this.discountEngine,
      this.configService,
      this.checkoutCoordinator,
      this.productService,
    );
  }

  async init() {
    try {
      // 1. 检查功能开关
      if (!this.configService.isEnabled()) {
        console.log("黑五活动未启用");
        return;
      }

      // 2. 解析并渲染阶梯满减规则
      const tiersConfig = this.configService.get("tiers");
      const tiersTitle = this.configService.get("tiersTitle");
      this.discountEngine.parseTiers(tiersConfig);
      Renderers.renderTiers(this.discountEngine.getTiers(), tiersTitle);

      // 3. 设置排除商品
      const excludeTitles = this.configService.get("excludeTitles");
      this.discountEngine.setExcludedTitles(excludeTitles);

      // 4. 加载商品列表
      const collectionHandle = this.configService.get("collectionHandle");
      if (!collectionHandle) {
        console.warn("未配置产品集合");
        return;
      }

      const limit = this.configService.get("limit", 100);
      const products = await this.productSelector.loadProductList(
        collectionHandle,
        limit,
      );

      if (products.length === 0) {
        console.warn("产品集合为空");
        return;
      }
      // 5. 渲染下拉选项（并发处理）
      await Promise.all([
        Renderers.renderDropdownOptions(
          1,
          products,
          this.productService,
          this.configService,
        ),
        Renderers.renderDropdownOptions(
          2,
          products,
          this.productService,
          this.configService,
        ),
      ]);

      // 6. 应用默认选择
      await this.uiController.applyDefaultSelections();

      // 7. 绑定所有事件
      this.uiController.bindEvents();

      // console.log("✅ 黑五活动页初始化成功");
    } catch (error) {
      console.error("❌ 黑五活动页初始化失败:", error);
      const errorEl = DOMUtils.qs(".bf-error");
      if (errorEl) {
        errorEl.textContent = "页面加载失败，请刷新重试";
      }
    }
  }

  destroy() {
    this.uiController.destroy();
  }
}

/* --------------------------
 * 🎬 应用启动入口
 * -------------------------- */
(function bootstrap() {
  let app = null;

  const startApp = () => {
    app = new BlackFridayApp();
    app.init();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startApp);
  } else {
    startApp();
  }

  // 暴露到全局（便于调试）
  if (typeof window !== "undefined") {
    window.BlackFridayApp = app;
  }
})();